import { useCallback, useEffect, useRef, useState } from "react";

const ZERO_OFFSET = { x: 0, y: 0 };

/**
 * Makes an antd `Modal` draggable by its title bar, without adding a new
 * dependency (plain mouse events — the same technique used by the
 * VolSurface 3D popup). Position resets to centered every time the modal
 * opens, and dragging is clamped so the modal can't be dragged fully
 * off-screen.
 *
 * Usage:
 *   const { modalRender, title } = useDraggableModal(open, "My Title");
 *   <Modal modalRender={modalRender} title={title} ... />
 */
export default function useDraggableModal(open, titleContent) {
  const [offset, setOffset] = useState(ZERO_OFFSET);
  const offsetRef = useRef(offset);
  offsetRef.current = offset;
  const dragStartRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (open) setOffset(ZERO_OFFSET);
  }, [open]);

  const clampOffset = useCallback((next) => {
    const node = wrapRef.current;
    if (!node) return next;
    const rect = node.getBoundingClientRect();
    const baseLeft = rect.left - offsetRef.current.x;
    const baseTop = rect.top - offsetRef.current.y;
    const minX = -baseLeft;
    const maxX = window.innerWidth - rect.width - baseLeft;
    const minY = -baseTop;
    const maxY = window.innerHeight - rect.height - baseTop;
    return {
      x: Math.min(Math.max(next.x, Math.min(minX, maxX)), Math.max(minX, maxX)),
      y: Math.min(Math.max(next.y, Math.min(minY, maxY)), Math.max(minY, maxY)),
    };
  }, []);

  const onMouseMove = useCallback(
    (e) => {
      const start = dragStartRef.current;
      if (!start) return;
      setOffset(
        clampOffset({
          x: start.offsetX + (e.clientX - start.x),
          y: start.offsetY + (e.clientY - start.y),
        })
      );
    },
    [clampOffset]
  );

  const onMouseUp = useCallback(() => {
    dragStartRef.current = null;
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
  }, [onMouseMove]);

  const onMouseDown = useCallback(
    (e) => {
      // Don't hijack clicks on the close (x) button or other header controls.
      if (e.target.closest(".ant-modal-close")) return;
      e.preventDefault();
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        offsetX: offsetRef.current.x,
        offsetY: offsetRef.current.y,
      };
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [onMouseMove, onMouseUp]
  );

  useEffect(
    () => () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    },
    [onMouseMove, onMouseUp]
  );

  const modalRender = useCallback(
    (node) => (
      <div
        ref={wrapRef}
        style={{ transform: `translate(${offset.x}px, ${offset.y}px)` }}
      >
        {node}
      </div>
    ),
    [offset]
  );

  const title = (
    <div className="draggable-modal-handle" onMouseDown={onMouseDown}>
      {titleContent}
    </div>
  );

  return { modalRender, title };
}
